'use client';

import { cn, getColorway } from 'utils/styles';
import Details from 'components/shared/BaseAccordion/Details';
import RichTextCopy from 'components/shared/RichTextCopy/RichTextCopy';
import Summary from 'components/shared/BaseAccordion/Summary';
import { SharedBentoBoxCardProps } from 'types/sharedComponents';
import { getColorwayName, getRichTextValue } from 'utils/formatters';
import { liveUpdateAttrs } from 'libs/contentful/utils';
import { useState } from 'react';
import { BentoBoxCardHeadlineSize, BentoBoxLayout, Colorways } from 'types/constants';

interface Props extends SharedBentoBoxCardProps {
  headlineSize?: BentoBoxCardHeadlineSize;
  isLargeCard?: boolean;
  layout?: BentoBoxLayout;
  mobileCopyExpanded?: boolean;
  classNames?: string;
}

const BentoBoxCard = ({
  sys,
  color,
  cardHeadlineRte,
  headlineSize,
  copy,
  isLargeCard,
  layout,
  mobileCopyExpanded,
  classNames,
}: Props) => {
  const [open, setIsOpen] = useState(false);

  const hasHeadlineAndCopy = cardHeadlineRte && copy;
  const displayMobileAccordion = hasHeadlineAndCopy && !mobileCopyExpanded;
  const labelText = cardHeadlineRte ? String(getRichTextValue(cardHeadlineRte)) : '';
  const isThreeByTwoLayout = layout === BentoBoxLayout.ThreeByTwo;

  const isLargeHeadline = isLargeCard || headlineSize === BentoBoxCardHeadlineSize.Large;
  const isExtraLargeHeadline = headlineSize === BentoBoxCardHeadlineSize.ExtraLarge;
  const isReserveColorway = getColorwayName(color) === Colorways.Reserve;

  const header = (
    <div
      className={cn('font-sans font-semibold text-heading-sm', {
        'md:text-heading-md': isLargeHeadline,
        'text-heading-md md:text-heading-xl font-display': isExtraLargeHeadline,
        'text-bg-action-primary': isReserveColorway,
      })}
      {...liveUpdateAttrs(sys?.id, 'cardHeadlineRte')}>
      <RichTextCopy copy={cardHeadlineRte} />
    </div>
  );
  const body = (
    <div className="font-sans font-medium" {...liveUpdateAttrs(sys?.id, 'cardCopy')}>
      <RichTextCopy copy={copy}></RichTextCopy>
    </div>
  );

  const copyWrapper = (paddingOverride?: string) => (
    <div className={cn('pt-6', paddingOverride)} data-testid="mobile-bento-card-copy-wrapper">
      {body}
    </div>
  );

  const mainCopy = copy && (mobileCopyExpanded ? copyWrapper('md:pt-0') : body);

  const parentClassName = cn(
    'flex h-full rounded-lg p-6 col-span-2 sm:col-span-1 sm:aspect-3/2',
    isLargeCard ? 'md:items-stretch md:rounded-3xl md:p-12' : 'md:items-center',
    classNames,
    !isThreeByTwoLayout && 'md:aspect-square',
    getColorway(color),
  );

  const columnClassName = cn(
    displayMobileAccordion && 'hidden',
    'w-full sm:flex sm:h-full sm:flex-col',
    isLargeCard && 'sm:justify-between',
    {
      'h-[11rem]': hasHeadlineAndCopy && !mobileCopyExpanded && !isLargeCard,
      'h-[11.5rem]': isLargeCard && !mobileCopyExpanded,
      'md:min-h-[11rem]': hasHeadlineAndCopy && mobileCopyExpanded && !isLargeCard,
      'md:min-h-[11.5rem]': isLargeCard && mobileCopyExpanded,
      'h-full': !hasHeadlineAndCopy,
    },
  );

  const mobileAccordion = displayMobileAccordion && (
    <div className="sm:hidden block w-full" data-testid="mobile-bento-card">
      <Details open={open}>
        <Summary
          openIcon="add_circle"
          closeIcon="remove_circle"
          label={labelText}
          isActive={open}
          onClick={setIsOpen}
          data-testid="mobile-bento-card-summary">
          {header}
        </Summary>
        {copyWrapper()}
      </Details>
    </div>
  );

  return (
    <div className={parentClassName}>
      <div className={columnClassName}>
        {cardHeadlineRte && header}
        {isLargeCard ? mainCopy : <div className={cn('grow content-end')}>{mainCopy}</div>}
      </div>
      {mobileAccordion}
    </div>
  );
};

export default BentoBoxCard;
